/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { when } from 'mobx';
import { fileService } from 'src/core/services/api/fileService';
import { FileType } from 'src/core/types/FileType';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { createChunkingAsyncHandler } from 'src/core/utils/api/chunkingAsyncHandler';
import { batchPromises } from 'src/core/utils/batchPromises';
import { getEventBus } from 'src/core/utils/eventBus';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getAppVersionService } from 'src/luis/core/services/apps/appVersionService';
import { getEntityService } from 'src/luis/core/services/entities/commonEntityService';
import { entitySwaggerToEntityStore } from 'src/luis/core/services/entities/mappers';
import { getIntentService } from 'src/luis/core/services/intents/intentService';
import { getPatternService } from 'src/luis/core/services/patterns/patternService';
import { ConversationPublishSlotStore } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { appVersionSwaggerToAppVersionStore } from 'src/luis/modules/apps/handlers/luisAppMappers';
import { getPhraseListService } from 'src/luis/modules/features/services/phraseListService';
import { intentSwaggerToIntentStore } from 'src/luis/modules/intents/intentMappers';
import { patternSwaggerToPatternStore } from 'src/luis/modules/patterns/patternMappers';
import { AppVersionSettings } from 'src/luis/projects/apps/types/AppVersionSettings';
import { instanceOfDeNormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { ModelType } from 'src/luis/types/ModelType';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

export type LuisAppVersionHandlerGroup = ReturnType<typeof createLuisAppVersionHandlerGroup>;

/**
 * Creates a handler group of functions for the app versions to
 * be fired by the dispatcher.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createLuisAppVersionHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const eventBus = getEventBus();

    eventBus.on('notifyTrainingSucceeded', ({ appId }) => {
        const currentAppStore = rootStore.currentLuisAppStore;

        if (currentAppStore?.id === appId) {
            currentAppStore.currentAppVersionStore.setDirty(false);
        }
    });

    /**
     * Helper: Gets the app store that corresponds to the given app id.
     *
     * @param param0.appId The id of the application to get.
     * @returns The app with the provided id.
     */
    const getAppStore = ({ appId }: Pick<VersionedAppId, 'appId'>) => rootStore.luisAppStoreHouse.findById(appId);

    /**
     * Helper: Gets the app version store that corresponds to the given app id
     * and version id.
     *
     * @param param0.appId The id of the application to get.
     * @param param0.versionId The id of the version to get.
     */
    const getAppVersionStore = ({ appId, versionId }: VersionedAppId) =>
        rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);

    /**
     * Loads app versions that belong to an app.
     *
     * @param param0.appId The app id.
     */
    const loadAppVersions = async (params?: HandlerParams) => {
        const { context } = params;
        const { appId } = context;
        const appStore = getAppStore({ appId });

        try {
            appStore.appVersionStoreHouse.tracker.setState('running');
            const appVersionStores = await getAppVersionService({ context }).getAppVersions();
            appStore.appVersionStoreHouse.setItems(appVersionStores);
            appStore.appVersionStoreHouse.tracker.setState('completed');
        } catch (error) {
            appStore.appVersionStoreHouse.tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    /**
     * Loads app version data.
     *
     * @param param0.appId The app id.
     * @param param0.version The version id.
     */
    const loadAppVersionData = async (params?: HandlerParams) => {
        const { context } = params;
        const { appId, versionId } = context;
        const appStore = rootStore.cluMigrationStoreHouse.luisMigratedAppsStoreHouse.findById(appId).luisApp;

        try {
            if (!appStore.appVersionStoreHouse.items.length) {
                const appVersionStores = await getAppVersionService({ context }).getAppVersions();
                appStore.appVersionStoreHouse.setItems(appVersionStores);
            }

            const { entityStoreHouse, intentStoreHouse, phraseListStoreHouse, patternStoreHouse } = getAppVersionStore({
                appId,
                versionId
            });

            if (!entityStoreHouse.isLoading && !entityStoreHouse.isLoaded) {
                const entityService = getEntityService({ context });
                entityStoreHouse.setIsLoading(true);
                const { entities, labelsPerEntity } = await entityService.getEntities();
                const entityStores = entities.filter(Boolean).map(entity => entitySwaggerToEntityStore(entity, labelsPerEntity[entity.id]));
                entityStores.forEach(eS => {
                    if (eS.typeId === ModelType.Simple) {
                        (<SimpleEntityStore>eS).children.forEach(c => instanceOfDeNormalizer(c, entityStores));
                    }
                });
                entityStoreHouse.setItems(entityStores);
                entityStoreHouse.setIsLoaded(true);
                entityStoreHouse.setIsLoading(false);
            }

            if (!intentStoreHouse.isLoading && !intentStoreHouse.isLoaded) {
                const intentService = getIntentService({ context });
                intentStoreHouse.setIsLoading(true);
                const intentSwaggers = await intentService.getIntents();
                const labelsPerIntentSwagger = await intentService.getLabelsPerIntentCount();
                const intentStores = intentSwaggers.map(intent => intentSwaggerToIntentStore(intent, labelsPerIntentSwagger[intent.id]));
                intentStoreHouse.setItems(intentStores);
                intentStoreHouse.setIsLoaded(true);
                intentStoreHouse.setIsLoading(false);
            }

            if (!phraseListStoreHouse.isLoading && !phraseListStoreHouse.isLoaded) {
                phraseListStoreHouse.setIsLoading(true);
                const phraseListService = getPhraseListService({ context });
                const phraseListStores = await phraseListService.getPhraseLists();
                phraseListStoreHouse.setItems(phraseListStores);
                phraseListStoreHouse.setIsLoaded(true);
                phraseListStoreHouse.setIsLoading(false);
            }

            if (!patternStoreHouse.isLoading && !patternStoreHouse.isLoaded) {
                if (intentStoreHouse.isLoading) {
                    await when(() => intentStoreHouse.isLoaded === true);
                }
                const patternService = getPatternService({ context });
                patternStoreHouse.setIsLoading(true);
                const patternSwaggers = await patternService.getPatterns();
                const patternStores = patternSwaggers.map(swagger =>
                    patternSwaggerToPatternStore(swagger, intentStoreHouse.findByName(swagger.intent).id)
                );
                patternStoreHouse.setItems(patternStores);
                patternStoreHouse.setIsLoaded(true);
                patternStoreHouse.setIsLoading(false);
            }
        } catch (error) {
            appStore.appVersionStoreHouse.tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    /**
     * Initiates the app version chunking process and loads the
     * fetched app versions into the store.
     */
    const loadAppVersionsByChunking = async (params?: HandlerParams) => {
        const { context } = params;
        const currentAppStore = rootStore.luisAppStoreHouse.findById(context.appId);

        if (currentAppStore.appVersionStoreHouse.isChunking) {
            return;
        }

        currentAppStore.appVersionStoreHouse.setIsChunking(true);
        const currentVersion = currentAppStore.currentAppVersionStore;
        currentAppStore.appVersionStoreHouse.setItems([currentVersion]);

        const handler = createChunkingAsyncHandler({
            storeHouse: currentAppStore.appVersionStoreHouse,
            mapper: appVersionSwaggerToAppVersionStore,
            excludedResourceIds: [currentVersion.id]
        });

        await getAppVersionService({ context }).getAppVersionsByChunking(handler, LuisConstants.ResourceLimits.ChunkingServiceLimit);
    };

    /**
     * Cancels any existing chunking operation.
     */
    const cancelAppVersionsChunking = async (params?: HandlerParams) => {
        const { context } = params;
        const currentAppStore = rootStore.luisAppStoreHouse.findById(context.appId);
        currentAppStore.appVersionStoreHouse.setIsChunking(false);
    };

    /**
     * Imports an app version.
     *
     * @param param0.appVersionJson The json representing an app version.
     */
    const importAppVersion = async ({
        file,
        fileType,
        optionalVersionId,
        context
    }: HandlerParams<{ file: File; optionalVersionId: string; fileType: FileType }>) => {
        const { appId } = context;
        const fileData = await fileService.readFromDisk(file);
        const appVersionService = getAppVersionService({ context });

        const importedAppVersionId = await appVersionService.importAppVersionByVersion({
            fileData,
            fileType,
            id: optionalVersionId
        });
        const importedAppVersion = await appVersionService.getAppVersionById({ id: importedAppVersionId });

        const appStore = getAppStore({ appId });
        appStore.appVersionStoreHouse.add(importedAppVersion);
    };

    /**
     * Exports an app version.
     *
     * @param param0.fileType The file type to export
     */
    const exportAppVersion = async ({ fileType, context }: HandlerParams<{ fileType: FileType }>) => {
        const { appId, versionId } = context;

        const appName = rootStore.luisAppStoreHouse.findById(appId).name;

        await getAppVersionService({ context }).exportAppVersion({ appName, versionId, fileType });
    };

    /**
     * Activates an app version.
     */
    const activateAppVersion = async ({ context, redirectUrl }: HandlerParams<{ redirectUrl: string }>) => {
        const { versionId } = context;

        await getAppVersionService({ context }).activateAppVersion({ id: versionId });

        window.location.replace(redirectUrl);
    };

    /**
     * Updates an existing app.
     *
     * @param param0 New app version name.
     */
    const renameAppVersion = async ({ newVersion, redirectUrl, context }: HandlerParams<{ newVersion: string; redirectUrl?: string }>) => {
        const { appId, versionId } = context;

        const appStore = rootStore.luisAppStoreHouse.findById(appId);
        const appVersionStore = getAppVersionStore({ appId, versionId });
        const isActiveVersion = versionId === appStore.activeVersion;

        await getAppVersionService({ context }).renameAppVersion({ id: versionId, version: newVersion });

        /**
         * Here we have two options:
         * 1. Update every thing manually ==> we decided not to do that.
         * 2. Refresh page as renaming the active version is considered
         *    switching to different version ==> The one we follow.
         */
        if (redirectUrl && isActiveVersion) {
            window.location.replace(redirectUrl);
        }

        appVersionStore.rename(newVersion);

        // Update the publish states to indicate the publish state of this version if it was published.
        if ((<ConversationPublishSlotStore>appStore.publishSlots.staging)?.versionId === versionId) {
            appStore.setPublishSlotMetadata(true, { ...appStore.publishSlots.staging, versionId: newVersion });
        }

        if ((<ConversationPublishSlotStore>appStore.publishSlots.production)?.versionId === versionId) {
            appStore.setPublishSlotMetadata(false, { ...appStore.publishSlots.production, versionId: newVersion });
        }
    };

    /**
     * Clones an existing app version.
     *
     * @param param0.newVersion New app version name.
     */
    const cloneAppVersion = async ({ newVersion, redirectUrl, context }: HandlerParams<{ newVersion: string; redirectUrl: string }>) => {
        const { versionId } = context;

        await getAppVersionService({ context }).cloneAppVersion({ id: versionId, version: newVersion });

        await activateAppVersion({ context: { ...context, versionId: newVersion }, redirectUrl });
    };

    /**
     * Deletes the app versions with the given ids from server.
     *
     * @param param0.appIds The ids of the apps to be deleted.
     */
    const deleteAppVersions = async ({ versionIdsToDelete, context }: HandlerParams<{ versionIdsToDelete: string[] }>) => {
        const { appId } = context;
        const appVersionService = getAppVersionService({ context });
        const appStore = rootStore.luisAppStoreHouse.findById(appId);
        const currentActiveVersionIndex = versionIdsToDelete.indexOf(appStore.currentAppVersionStore.id);

        const nonDeletedVersions = appStore.appVersionStoreHouse.items
            .slice()
            .filter(version => versionIdsToDelete.every(id => version.id !== id));

        if (currentActiveVersionIndex !== -1) {
            const newVersionId = nonDeletedVersions.sort((a, b) => (a.modifiedDate < b.modifiedDate ? 1 : -1))[0].id;
            appStore.setActiveVersion(newVersionId);

            await appVersionService.activateAppVersion({ id: newVersionId });
        }

        await batchPromises(versionIdsToDelete.map(id => appVersionService.deleteAppVersion({ id })));
        appStore.appVersionStoreHouse.batchRemoveByIds(versionIdsToDelete);
    };

    /**
     * Fetches an app version settings.
     */
    const getAppVersionSettings = async (params?: HandlerParams) => {
        const { context } = params;
        const { appId, versionId } = context;

        const settings = await getAppVersionService({ context }).getAppVersionSettingsById({ id: versionId });
        const appVersionStore = getAppVersionStore({ appId, versionId });

        appVersionStore.setSettings(settings);
    };

    /**
     * Updates an app version settings.
     *
     * @param param0.settings The app version new settings.
     */
    const updateAppVersionSettings = async ({ settings, context }: HandlerParams<{ settings: AppVersionSettings }>) => {
        const { appId, versionId } = context;
        const appVersionStore = getAppVersionStore({ appId, versionId });

        const newSettingNames = settings.map(setting => setting.name);
        const newSettings = appVersionStore.settings
            .filter(setting => newSettingNames.every(name => name !== setting.name))
            .concat(settings);

        await getAppVersionService({ context }).updateAppVersionSettings({ id: versionId, settings: newSettings });

        appVersionStore.setSettings(newSettings);
    };

    return {
        activateAppVersion,
        loadAppVersions,
        loadAppVersionData,
        loadAppVersionsByChunking,
        cancelAppVersionsChunking,
        importAppVersion,
        exportAppVersion,
        renameAppVersion,
        cloneAppVersion,
        deleteAppVersions,
        getAppVersionSettings,
        updateAppVersionSettings
    };
};
